import { Outlet, createFileRoute } from "@tanstack/react-router";

import { LANDING_PATHS } from "@/content/landing/paths";

/**
 * The segment the root-level marketing pages live on.
 *
 * A single dynamic segment rather than a file per page, because the ticket's
 * standard is that adding a page is a content change: a `landingPage` document
 * in the Studio plus a run of `scripts/refresh-sanity-index.mjs`, which writes
 * its path into `paths.ts`, publishes a page, and unpublishing the document
 * removes it.
 *
 * Two details make that work against the route tree this app already has.
 *
 * `/$planId` — the dashboard's plan scope under `_app/_dashboard` — is also a
 * bare root-level dynamic segment, and the router folds two identically-shaped
 * dynamic segments into ONE node, so whichever is declared first swallows every
 * root path. A `params.parse` gives this one a node of its own, sorts it ahead
 * of the plain one, and lets it decline: `false` reads as "no match" and the
 * router falls through to the plan scope, which is what `/12345` still needs.
 * It must RETURN false rather than throw — `validateParseParams` swallows a
 * throw and takes the match anyway.
 *
 * It declines only what could BE a plan id. Falling through on anything
 * unrecognised handed every mistyped marketing URL to the plan scope, which
 * sits behind `RouteGuard`: a reader who typed `/pricng` was answered with a
 * login form instead of "not found". A non-numeric segment is no plan id —
 * `usePlanId` collapses it to `"default"` — so this route keeps it and renders
 * its own 404.
 *
 * And the page itself is the `index` child rather than this route, because the
 * router ranks an index frame above a plain one at the same depth: as a leaf
 * this route lost every path to `/$planId/`, which is one.
 */
export const Route = createFileRoute("/$landing")({
  params: {
    // The path list, not the pages themselves, which `landingPages.test.tsx`
    // holds to the same set: parsing runs on every root URL, and the pages are
    // in the CMS, which no browser navigation may reach for.
    parse: (raw: { landing: string }) =>
      LANDING_PATHS.includes(`/${raw.landing}`) || !/^\d+$/.test(raw.landing)
        ? raw
        : false,
  },
  component: Outlet,
});
